import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { strapiGet, strapiPut, strapiPost, strapiDelete } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import type { Table, TableCombination, TableMetadata } from '@/lib/types/table'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'
import { format } from 'date-fns'

function wrapList<T>(data: T[] | undefined) {
  return {
    data: data !== undefined ? { data, meta: { pagination: { total: data.length, page: 1, pageSize: data.length, pageCount: 1 } } } : undefined,
    isLoading: data === undefined,
  }
}

async function syncTableIntoAreas(table: Table) {
  const areas = await db.areas.toArray()
  await Promise.all(
    areas.map((area) => {
      const currentTables = area.tables ?? []
      const hadTable = currentTables.some((t) => t.documentId === table.documentId)
      const belongsToArea = table.area?.documentId
        ? area.documentId === table.area.documentId
        : hadTable
      const nextTables = currentTables.filter((t) => t.documentId !== table.documentId)

      if (belongsToArea && !table.deleted) nextTables.push(table)
      if (!hadTable && nextTables.length === currentTables.length) return Promise.resolve()
      return db.areas.put({ ...area, tables: nextTables })
    }),
  )
}

async function removeTableFromAreas(documentId: string) {
  const areas = await db.areas.toArray()
  await Promise.all(
    areas.map((area) => {
      const currentTables = area.tables ?? []
      const nextTables = currentTables.filter((t) => t.documentId !== documentId)
      if (nextTables.length === currentTables.length) return Promise.resolve()
      return db.areas.put({ ...area, tables: nextTables })
    }),
  )
}

async function updateAreaEmbeddedTableMetadata(
  updates: Array<{ documentId: string; metadata: TableMetadata }>,
) {
  const updateByDocumentId = new Map(updates.map((u) => [u.documentId, u.metadata]))
  const areas = await db.areas.toArray()
  await Promise.all(
    areas.map((area) => {
      let changed = false
      const nextTables = (area.tables ?? []).map((table) => {
        const metadata = updateByDocumentId.get(table.documentId)
        if (!metadata) return table
        changed = true
        return { ...table, metadata: { ...(table.metadata ?? {}), ...metadata } }
      })
      if (!changed) return Promise.resolve()
      return db.areas.put({ ...area, tables: nextTables })
    }),
  )
}

export function useTables() {
  const data = useLiveQuery(() => db.floorTables.toArray().then((r) => r.sort((a, b) => a.name.localeCompare(b.name))))
  return wrapList(data)
}

export function useUpdateTable() {
  return useMutation({
    mutationFn: async ({ documentId, ...data }: { documentId: string } & Partial<Table>) => {
      const resp = await strapiPut<Table>(`/api/tables/${documentId}`, { data })
      const table = (resp as any).data ?? resp
      if (table?.documentId) {
        await db.floorTables.put(table)
        await syncTableIntoAreas(table)
      }
      return resp
    },
    onSuccess: () => toast.success('Table updated'),
    onError: () => toast.error('Failed to update table'),
  })
}

export function useCreateTable() {
  return useMutation({
    mutationFn: async (data: Partial<Table>) => {
      const resp = await strapiPost<Table>('/api/tables', { data })
      const table = (resp as any).data ?? resp
      if (table?.documentId) {
        await db.floorTables.put(table)
        await syncTableIntoAreas(table)
      }
      return resp
    },
    onSuccess: () => toast.success('Table created'),
    onError: () => toast.error('Failed to create table'),
  })
}

export function useSoftDeleteTable() {
  return useMutation({
    mutationFn: async ({ id, documentId }: { id: number; documentId: string }) => {
      const today = format(new Date(), 'yyyy-MM-dd')
      const check = await strapiGet<any[]>('/api/bookings', {
        filters: {
          date: { $gte: today },
          bookingTables: { table: { id: { $eq: id } } },
        },
        pagination: { pageSize: 1 },
      })
      const count = (check as any)?.meta?.pagination?.total ?? (check as any)?.data?.length ?? 0
      if (count > 0) {
        throw new Error(`This table has ${count} upcoming booking(s) and cannot be deleted.`)
      }
      const result = await strapiPut(`/api/tables/${documentId}`, { data: { deleted: true } })
      await db.floorTables.delete(documentId)
      await removeTableFromAreas(documentId)
      return result
    },
    onSuccess: () => toast.success('Table deleted'),
    onError: (err: Error) => toast.error(err.message || 'Failed to delete table'),
  })
}

export function useBatchUpdateTablePositions() {
  return useMutation({
    mutationFn: async (updates: Array<{ documentId: string; metadata: { posX: number; posY: number; rotation: number } }>) => {
      const mergedUpdates = await Promise.all(
        updates.map(async ({ documentId, metadata }) => {
          const table = await db.floorTables.get(documentId)
          const mergedMetadata: TableMetadata = { ...(table?.metadata ?? {}), ...metadata }
          return { documentId, metadata: mergedMetadata }
        }),
      )
      // Update Dexie immediately for instant UI feedback
      await Promise.all(
        mergedUpdates.map(({ documentId, metadata }) =>
          (db.floorTables as any).update(documentId, { metadata }),
        ),
      )
      await updateAreaEmbeddedTableMetadata(mergedUpdates)
      // Persist to server
      return Promise.all(
        mergedUpdates.map(({ documentId, metadata }) =>
          strapiPut(`/api/tables/${documentId}`, { data: { metadata } }),
        ),
      )
    },
    onError: () => toast.error('Failed to save layout — your changes are still pending, click Save to retry'),
    onSuccess: () => toast.success('Layout saved'),
  })
}

export function useTableCombinations() {
  const restaurantId = useAppStore((s) => s.restaurantId)
  return useQuery({
    queryKey: ['tableCombinations', restaurantId],
    queryFn: () =>
      strapiGet<TableCombination[]>('/api/table-combinations', {
        filters: { area: { restaurant: { documentId: { $eq: restaurantId } } } },
        populate: { tables: true, area: true },
        pagination: { pageSize: 100 },
      }),
    enabled: !!restaurantId,
    staleTime: 5 * 60_000,
  })
}

export function useCreateTableCombination() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; tables: number[]; area: number; bookosId: string; seatsMin: number; seatsMax: number; bookable: boolean; bookableOnline: boolean; bookingPriority: number }) =>
      strapiPost<TableCombination>('/api/table-combinations', { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tableCombinations'] })
      toast.success('Tables combined')
    },
    onError: () => toast.error('Failed to combine tables'),
  })
}

export function useUpdateTableCombination() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ documentId, ...data }: { documentId: string; name?: string; seatsMin?: number; seatsMax?: number; tables?: number[] }) =>
      strapiPut<TableCombination>(`/api/table-combinations/${documentId}`, { data }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tableCombinations'] })
      toast.success('Combination updated')
    },
    onError: () => toast.error('Failed to update combination'),
  })
}

export function useDeleteTableCombination() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => strapiDelete(`/api/table-combinations/${documentId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['tableCombinations'] })
      toast.success('Combination removed')
    },
    onError: () => toast.error('Failed to remove combination'),
  })
}
